/**
 * Image Model Configuration
 * Defines available image choices for the story wizard with current pricing.
 */

export const imageModelOptions = [
  {
    id: 'none',
    name: 'Text only',
    description: 'Create a readable story without generating or storing illustrations.',
    apiModel: null,
    quality: 'none',
    costPerImage: 0,
    recommended: false,
    features: ['No image cost', 'Faster generation', 'Text-first reading'],
  },
  {
    id: 'gemini-3.1-flash-lite-image',
    name: 'Fast illustrations',
    description: 'Lower-cost 1K Grok Imagine illustrations with quick generation and storybook styling.',
    apiModel: 'spacexai/grok-imagine-image-2.0',
    quality: 'balanced',
    // grok-imagine-image-2.0 at 1K low; each portrait reference adds $0.01.
    costPerImage: 0.04,
    recommended: true,
    features: ['Lower cost', 'Fast generation', '1K storybook art'],
  },
  {
    id: 'gemini-3.1-flash-image',
    name: 'Rich illustrations',
    description: 'Higher-quality 2K Grok Imagine illustrations for more demanding scenes and composition.',
    apiModel: 'spacexai/grok-imagine-image-2.0',
    quality: 'high',
    // grok-imagine-image-2.0 at 1K medium; each portrait reference adds $0.01.
    costPerImage: 0.06,
    recommended: false,
    features: ['Stronger composition', 'Scene-aware art', 'Watercolor storybook style'],
  },
];

/**
 * Get model by ID
 * @param {string} modelId - The model ID
 * @returns {object|undefined} The model configuration
 */
export const getModelById = (modelId) => {
  return imageModelOptions.find(model => model.id === modelId);
};

/**
 * Get the API model name for a given model ID
 * @param {string} modelId - The model ID
 * @returns {string} The API model name
 */
export const getApiModelName = (modelId) => {
  const model = getModelById(modelId);
  return model?.apiModel || 'spacexai/grok-imagine-image-2.0';
};

/**
 * Get the recommended model
 * @returns {object} The recommended model configuration
 */
export const getRecommendedModel = () => {
  return imageModelOptions.find(model => model.recommended) || imageModelOptions.find(model => model.id !== 'none');
};

/**
 * Calculate estimated cost for generating images
 * @param {string} modelId - The model ID
 * @param {number} imageCount - Number of images to generate
 * @returns {number} Estimated cost in dollars
 */
export const estimateCost = (modelId, imageCount) => {
  const model = getModelById(modelId);
  if (!model) return 0;
  return model.costPerImage * imageCount;
};

export default imageModelOptions;
